import { Box, Text, ThemeContext, Anchor } from 'grommet';
import React, { useContext, useEffect, useState }from 'react';
import useSWR from 'swr';
import { INVOICE_CARD_MAX_WIDTH, INVOICE_CARD_MIN_WIDTH } from '../constants/Dimensions';
import { DarkenOnHoverBox, DisplayInlineBlockBox, EnhancedDropButton } from '../utils/styled';
import ContentLine from './ContentLine';
import { NotificationsContext } from './NotificationsProvider';
import { countNumberOfIssuedMilestones, getInvoiceStatusBadge, getOverdueRange, renderPOnumber } from '../utils/invoice';
import { getInvoiceStatus } from '../utils/business';
import { InfoLine } from './CustomerTag';
import { formatCurrency } from '../utils/numbers';
import { formatDate } from '../utils/dates';
import { useRouter } from 'next/router';
import { setInvoiceBackUrl } from '../utils/invoiceNavigation';
import {
    IconUser as IconMan,
    IconCoin as IconDollar,
    IconCalendar,
    IconFileDollar as IconFileInvoice,
    IconCalendarExclamation as IconCalendarDue,
    IconX as IconClose,
    IconShoppingBag as IconShopping,
    IconFlag,
    IconLink
} from '@tabler/icons-react';
import FontAwesomeIcon from './FontAwesomeIcon';
import Button from './Button';
import MilestoneProgressBar from './pages/invoices/MilestoneProgressBar';
import CopyLinkButton from './CopyLinkButton';

const InvoiceTag = ({id, onRemove = undefined, inline = false, prefix= null}) => {
    const theme = useContext(ThemeContext);
    const router = useRouter();
    const [isMilestoneInvoice, setIsMilestoneInvoice] = useState(false);

    const { data } = useSWR(
        `/api/invoices/${id}/tag`,
        (url) => fetch(url).then(res => {
            if (!res.ok) return null;
            return res.json();
        }),
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000, // 1 minute - reduce duplicate requests
        },
    );

    useEffect(() => {
        if (data?.billing_milestones && data?.billing_milestones.length > 0) {
            setIsMilestoneInvoice(true);
        }
    }, [data]);

    return (
        <DisplayInlineBlockBox>
            <EnhancedDropButton
                event={{section: 'invoice_tag', element: 'tag', description: data?.invoice_token}}
                label={(
                    <DarkenOnHoverBox
                        background={theme.dark ? "foreground" : "light-1"}
                        border={{color: "border"}}
                        round="xxsmall"
                        theme={theme}>
                        <Box direction="row" align="center" pad="xxsmall">
                            <Box pad={{horizontal: 'xxsmall'}} direction="row" align="center" gap="xsmall">
                                <Text wordBreak="keep-all">
                                    {prefix ? prefix : ''}
                                    {'#'}
                                    {data && (data?.identifier || '').replace(/^#/, '')}
                                </Text>
                                {!data && (<ContentLine width="xxsmall" isLoading={true} />)}
                            </Box>
                            {onRemove && (
                                <Box pad={{left: 'xxsmall', right: 'xsmall'}}>
                                    <Anchor
                                        plain
                                        hoverIndicator
                                        label={(<FontAwesomeIcon SelectedIcon={IconClose} size="xxxsmall" />)}
                                        onClick={() => onRemove(id)} />
                                </Box>
                            )}
                        </Box>
                    </DarkenOnHoverBox>
                )}
                plain
                hoverIndicator
                dropAlign={{top: 'bottom', left: 'left'}}
                dropProps={{border: true, margin: {vertical: 'xsmall'}, inline}}
                dropContent={data ? (
                    <Box pad="small" margin="small" width={{min: INVOICE_CARD_MIN_WIDTH, max: INVOICE_CARD_MAX_WIDTH}} flex={{shrink: 0}}>
                        <Box margin={{vertical: 'xsmall'}} border={{side: 'bottom'}} pad={{bottom: 'small'}} width={{min: INVOICE_CARD_MIN_WIDTH}}>
                            <Text size="large" weight={500} wordBreak="keep-all">
                                Invoice {data?.identifier}
                            </Text>
                            <Box pad={{vertical: 'xsmall'}}>
                                {getInvoiceStatusBadge({
                                    ...data,
                                    overdue_range: getOverdueRange(new Date(data?.due_date)),
                                    invoice_status: getInvoiceStatus(data?.due_date, data?.open_balance),
                                    theme
                                })}
                            </Box>
                        </Box>
                        <Box direction="column" gap="small" pad={{vertical: 'small'}}>
                            {renderPOnumber(data?.po_number) ? (
                                <InfoLine
                                    icon={IconShopping}
                                    description={`PO #${renderPOnumber(data.po_number)}`} />
                            ) : ''}
                            <InfoLine
                                icon={IconMan}
                                description={(<Anchor href={`/customers/${data.recipient_uuid}`}>{data?.recipient_name}</Anchor>)} />
                            <InfoLine
                                icon={IconFileInvoice}
                                description={(<Text>{formatCurrency(data?.amount, data?.currency)} invoice total</Text>)} />
                            {data?.open_balance !== data?.amount && data?.open_balance > 0 && (
                                <InfoLine
                                    icon={IconDollar}
                                    description={(<Text>{formatCurrency(data?.open_balance)} balance due</Text>)} />
                            )}
                            {data?.open_balance !== data?.amount && data?.open_balance < 0 && (
                                <InfoLine
                                    icon={IconDollar}
                                    description={(<Text>{formatCurrency(-data?.open_balance, data?.currency)} credit available</Text>)} />
                            )}
                            <InfoLine
                                icon={IconCalendar}
                                description={(<Text>Issued on {formatDate(data?.issued_at) || ''}</Text>)} />
                            {isMilestoneInvoice ? (
                                <>
                                    <InfoLine
                                        icon={IconFlag}
                                        description={(
                                            <Box>
                                                <Text>{countNumberOfIssuedMilestones(data?.billing_milestones)} of {data?.billing_milestones.length} part{data?.billing_milestones.length > 1 ? 's' : ''} issued</Text>
                                                {data?.next_open_milestone ? (<Text size="small" color="text-weak">
                                                    Next payment due on {formatDate(data.next_open_milestone?.due_date) || ''}
                                                </Text>) : ''}
                                            </Box>
                                        )} />

                                </>
                            ) : (
                                <InfoLine
                                    icon={IconCalendarDue}
                                    description={(<Text>Due on {formatDate(data?.due_date) || ''}</Text>)} />
                            )}
                            {isMilestoneInvoice && (
                                <InfoLine
                                    icon={IconDollar}
                                    description={(
                                        <Box>
                                            <Text>{formatCurrency(Math.min(data?.amount - data?.open_balance, data?.amount), data?.currency)} of {formatCurrency(data?.amount, data?.currency)} paid</Text>
                                            <Box margin={{top: 'small'}}>
                                                <MilestoneProgressBar
                                                    milestones={data?.billing_milestones}
                                                    invoiceTotal={data?.amount}
                                                    paidAmount={data?.amount - data?.open_balance} />
                                            </Box>
                                        </Box>
                                    )}/>
                            )}
                        </Box>
                        <Box margin={{top: 'small'}} direction="row" gap="xsmall">
                            <CopyLinkButton link={`${window.location.origin}/p/${data?.invoice_token}`} />
                            <Button
                                event={{section: 'invoice_tag', element: 'view_invoice', description: data?.invoice_token}}
                                primary
                                ctaColor="accent"
                                color="accent"
                                fill="horizontal"
                                label={(
                                    <Box direction="row" gap="small" align="center" fill="horizontal" justify="center">
                                        <Text color="text">View invoice</Text>
                                    </Box>
                                )}
                                onClick={() => {
                                    setInvoiceBackUrl(router.asPath);
                                    router.push(`/invoices/${data?.invoice_token}`);
                                }}
                            />
                        </Box>
                    </Box>
                ) : (<></>)}>
            </EnhancedDropButton>
        </DisplayInlineBlockBox>
    );
}

export default InvoiceTag;